import React, { CSSProperties, useState } from "react";
import { Button, Dropdown, Input, DropdownProps, InputProps, Form } from "semantic-ui-react";

import { useIntl } from "react-intl";

// Common
import ModConfirmCNMI from "react-lib/apps/common/cnmi/ModConfirm";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import EmployeeToken from "react-lib/apps/common/EmployeeToken";
import PasswordRecheck from "react-lib/apps/common/PasswordRecheck";

type ModCancelImagingOrderProps = {
  onEvent: (e: any) => any;
  setProp: (key: string, value: any, callback?: () => any) => any;
  // data
  open: boolean;
  action: string;
  orderStatus: string;
  title?: string;
  card: string;
  buttonKey?: string;
  backgroundColor?: CSSProperties["backgroundColor"];
  userFullname?: string;
  // CommonInterface
  buttonLoadCheck?: Record<string, any>;
  userTokenize?: Partial<{
    token: string;
    employeeName: string;
    loading: boolean;
    error: any;
  }> | null;
  // options
  cancelImagingOrderOptions?: Record<string, any>[];
  // config
  hideReason?: boolean;
  authUsrPass?: boolean;
  infoOnly?: boolean;
  // callback
  onApprove?: (data: ApproveDataType) => any;
  onClose?: () => any;
};

export type ApproveDataType = {
  reason: string | string[];
  username: string;
  password: string;
  onClear?: () => any;
};

// Const
const BUTTON_ACTIONS = {
  del: "CANCEL_ORDER",
} as const;

const ModCancelImagingOrder = (props: ModCancelImagingOrderProps) => {
  const intl = useIntl();
  const [reason, setReason] = useState<string | string[]>("");
  const [username, setUsername] = useState<string>("");
  const [password, setPassword] = useState<string>("");

  const handleChangeReason = (e: any, data: DropdownProps) => {
    setReason(data.value as string | string[]);
  };

  const handleClearToken = () => {
    props.setProp("userTokenize", {
      ...props.userTokenize,
      token: "",
      employeeName: "",
      loading: false,
      error: null,
    });
  };

  const handleClear = () => {
    setReason("");
    setUsername("");
    setPassword("");

    handleClearToken();
  };

  const handleCloseCancel = () => {
    props.onClose?.();

    handleClear();
  };

  const handleApprove = () => {
    props.onApprove?.({
      reason,
      username,
      password,
      onClear: () => {
        handleClear();
      },
    });
  };

  const handleChangeUsername = (e: any, data: InputProps) => {
    setUsername(data.value);
  };

  const handleEnterToken = (code: string) => {
    props.onEvent({
      message: "UserTokenize",
      params: {
        action: "CheckUserToken",
        code: code,
      },
    });
  };

  return (
    <div>
      <ModConfirmCNMI
        openModal={props.open}
        titleName={props.title ?? "Cancel Imaging order"}
        titleColor={!props.backgroundColor && "red"}
        backgroundColor={props.backgroundColor}
        size="small"
        denyButtonColor="red"
        denyButtonText="CANCEL"
        modalStyle={{ width: props.authUsrPass ? "35.5%" : "27.5%" }}
        customGridGroup={{ padding: props.authUsrPass ? "0 8%" : "" }}
        approveButton={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleApprove}
            // data
            paramKey={`${props.card}_${props.buttonKey || BUTTON_ACTIONS.del}`}
            buttonLoadCheck={
              props.buttonLoadCheck?.[`${props.card}_${props.buttonKey || BUTTON_ACTIONS.del}`]
            }
            // config
            id="ModCancelImagingOrder-ButtonLoadCheck-Confirm"
            disabled={
              // disabled เมื่อ ไม่ระบุเหตุผล และ ไม่ระบุ employee code หรือ ไม่กรอก username password หาก เป็น authUsrPass
              props.infoOnly
                ? false
                : props.hideReason
                ? props.authUsrPass
                  ? !password
                  : !props.userTokenize?.token
                : !reason ||
                  (props.authUsrPass ? !username || !password : !props.userTokenize?.token)
            }
            color={"green"}
            name={props.buttonKey || BUTTON_ACTIONS.del}
            size="medium"
            title="CONFIRM"
            basic={true}
            fluid={true}
          />
        }
        content={
          <div
            style={{
              margin: "-1rem 0.5rem -1.5rem 0.5rem",
              lineHeight: 1.75,
              textAlign: "center",
            }}
          >
            <div>
              Do you want to <strong>{props.action}</strong> this order ?
            </div>
            <div
              style={{
                marginBottom: props.hideReason && !props.infoOnly ? "1rem" : "",
              }}
            >
              The order status will be {props.orderStatus}
            </div>

            {/* แสดงเมื่อ ไม่ซ่อนเหตุผล และ แสดงเฉพาะ infomation เท่านั้น */}
            {!props.hideReason && !props.infoOnly && (
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  margin: "1rem 0 1rem",
                }}
              >
                <div style={{ minWidth: "max-content", marginRight: "1rem" }}>
                  Please specify<span style={{ color: "red" }}>*</span>
                </div>
                <Dropdown
                  id="ModCancelImagingOrder-Dropdown-Reason"
                  value={reason || ""}
                  options={props.cancelImagingOrderOptions}
                  search={true}
                  multiple={true}
                  selection={true}
                  fluid={true}
                  // style
                  style={{ width: "100%" }}
                  onChange={handleChangeReason}
                />
              </div>
            )}

            {!props.infoOnly && (
              <>
                {props.authUsrPass ? (
                  <Form>
                    <Form.Group inline style={{ marginBottom: 0 }}>
                      <Form.Field
                        className="--override-disabled --grey"
                        width={8}
                        style={{ display: "flex", alignItems: "center" }}
                      >
                        <label style={{ fontWeight: "normal" }}>Doctor</label>
                        <Input
                          id="ModCancelImagingOrder-Input-Username"
                          value={props.userFullname}
                          placeholder="Username"
                          fluid={true}
                          disabled
                          onChange={handleChangeUsername}
                        />
                      </Form.Field>
                      <Form.Field
                        width={9}
                        required
                        style={{ display: "flex", alignItems: "center", padding: 0 }}
                      >
                        <label style={{ minWidth: "max-content", fontWeight: "normal" }}>
                          Password
                        </label>
                        <PasswordRecheck id="ModCancelImagingOrder-PasswordRecheck-Password" fluid password={password} setPassword={setPassword} />
                      </Form.Field>
                    </Form.Group>
                  </Form>
                ) : (
                  <div>
                    <EmployeeToken
                      id="ModCancelImagingOrder-EmployeeToken-Code"
                      placeholder={"Employee code"}
                      onEnterToken={handleEnterToken}
                      onClearToken={handleClearToken}
                      error={!!props.userTokenize?.error}
                      loading={props.userTokenize?.loading}
                      employeeName={props.userTokenize?.employeeName}
                      fluid={true}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        }
        onDeny={handleCloseCancel}
        onCloseWithDimmerClick={handleCloseCancel}
      />
    </div>
  );
};

export default React.memo(ModCancelImagingOrder);
